import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "../auth";
import { listMyGardens } from "../db/gardenMembership";
import { getOrCreateGarden } from "../db/gardens";
import type { GardenRow } from "@garden/types/db";

const STORAGE_KEY = "garden-app:current-garden-id";

interface CurrentGardenState {
  /** All gardens the user is a member of. Empty until first load completes. */
  gardens: GardenRow[];
  /** The currently active garden, or null when the user has none. */
  current: GardenRow | null;
  /** Loading state for the very first fetch. */
  loading: boolean;
  /** Pick a different garden as the active one. Persists in localStorage. */
  setCurrentGardenId: (id: string) => void;
  /** Reload gardens (e.g. after Join or Leave). */
  refresh: () => Promise<void>;
}

const CurrentGardenContext = createContext<CurrentGardenState | null>(null);

export function CurrentGardenProvider({ children }: { children: ReactNode }) {
  const [gardens, setGardens] = useState<GardenRow[]>([]);
  const [currentId, setCurrentIdState] = useState<string | null>(() => {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(STORAGE_KEY);
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!supabase) return;
    // Make sure the user has a garden at all. If they have none — first-time
    // login, or just left their last one — bootstrap a fresh one. The
    // bootstrap also adds them as a member.
    let list = await listMyGardens(supabase);
    if (list.length === 0) {
      await getOrCreateGarden(supabase, "My garden");
      list = await listMyGardens(supabase);
    }
    setGardens(list);
    setCurrentIdState((prev) => {
      // Keep the existing selection if still valid; else fall back to the
      // first (oldest) garden.
      if (prev && list.some((g) => g.id === prev)) return prev;
      const firstId = list[0]?.id ?? null;
      return firstId;
    });
  }, []);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        await refresh();
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  // Persist selected id whenever it changes.
  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    if (currentId) localStorage.setItem(STORAGE_KEY, currentId);
    else localStorage.removeItem(STORAGE_KEY);
  }, [currentId]);

  const setCurrentGardenId = useCallback((id: string) => {
    setCurrentIdState(id);
  }, []);

  const current = useMemo(
    () => gardens.find((g) => g.id === currentId) ?? null,
    [gardens, currentId],
  );

  const value = useMemo<CurrentGardenState>(
    () => ({ gardens, current, loading, setCurrentGardenId, refresh }),
    [gardens, current, loading, setCurrentGardenId, refresh],
  );

  return createElement(CurrentGardenContext.Provider, { value }, children);
}

export function useCurrentGarden(): CurrentGardenState {
  const ctx = useContext(CurrentGardenContext);
  if (!ctx) {
    throw new Error("useCurrentGarden called outside CurrentGardenProvider");
  }
  return ctx;
}

/** Convenience selector: just the current garden's id, or null. */
export function useCurrentGardenId(): string | null {
  return useCurrentGarden().current?.id ?? null;
}
